import Tooltip from "@Components/Tooltip/Tooltip";
import styles from "./InventoryPaymentSideCard.module.scss";
import formatClassNames from "@Utils/clientUtils/stringUtility/formatClassNames";
import InfoIcon from "@Icons/InfoIcon";
import wrapperHOC from "@Utils/wrapperHOC";
import useTranslationHook from "hooks/useTranslationHook";
import getCurrencySymbol from "helpers/getCurrencySymbol";
import config from "@Config/index";
import { getTotalPaymentValue } from "@Pages/Payment/utils";
import capitalizeFirstLetter from "@Utils/capitalizeFirstLetter";
import useIsLanguage from "hooks/useIsLanguage";
import { useSelector } from "react-redux";
import { useEffect, useState } from "react";
import { AppState } from "@Store/index";
import { findCurrency } from "../utils";
import fetchChinaPaymentAmount from "@Pages/Payment/utils/fetchChinaPaymentAmount";

interface InventoryPaymentSideCardProps {
  data?: {
    currency: string;
    deposit: string;
    processingFees: string;
    totalAmount: string;
    paymentSummaryLabel: string;
    paymentTypeText: string;
    paymentType: string;
    payment_info: any;
  };
  isDesktop?: boolean;
  isConfirmation?: boolean;
  isInSideInventoryCard?: boolean;
  containerClassName?: string;
  totalPayableText?: string;
  customTitle?: string;
  footerNote?: {
    text: string | React.ReactNode;
    className?: string;
  };
  hideConversionInfo?: boolean;
}

const getTotalText = ({ isConfirmation = false }) => {
  if (isConfirmation) return "bookform.formlayout.inventorypaymentcard.totalpayableamount";
  return "bookform.formlayout.inventorypaymentcard.payablenow";
};

const InventoryPaymentSideCard: React.FC<InventoryPaymentSideCardProps> = ({
  data,
  isDesktop,
  isConfirmation,
  isInSideInventoryCard = false,
  containerClassName = "",
  customTitle,
  footerNote,
  hideConversionInfo = false,
  totalPayableText = "",
}) => {
  const { inventoryData, paymentTokenData } = useSelector(({ bookFormData }: AppState) => ({
    inventoryData: bookFormData?.data,
    paymentTokenData: bookFormData?.paymentTokenData?.data,
  }));
  const [chinaPaymentAmount, setChinaPaymentAmount] = useState<string | null>(null);
  const { t } = useTranslationHook();
  const userLanguage = useIsLanguage();
  const priceSummaryText = t("formlayout.inventorypaymentsidecard.bookingamounttext");
  const paymentInfoData =
    data?.payment_info?.length > 0
      ? data?.payment_info.filter(
          (payment: any) =>
            payment.type !== "total" &&
            !(config.IS_CHINA_APP && payment.type === "gateway_charges"),
        )
      : [];
  const totalCharges = getTotalPaymentValue(data?.payment_info);
  const paymentTotalRow = data?.payment_info?.find((payment: any) => payment?.type === "total");
  const propertyCurrency = findCurrency(
    paymentTotalRow?.original_currency || paymentTotalRow?.currency,
  );
  const propertyCurrencyCode =
    propertyCurrency?.standard_name?.toLowerCase() ||
    paymentTotalRow?.original_currency?.toLowerCase() ||
    paymentTotalRow?.currency?.toLowerCase() ||
    "";
  // China app: exclude gateway_charges (same as getTotalPaymentValue / backend charge base).
  const propertyAmount = totalCharges?.amount;

  useEffect(() => {
    let cancelled = false;

    if (!config.IS_CHINA_APP || !propertyCurrencyCode || propertyCurrencyCode === "cny") {
      setChinaPaymentAmount(null);
    } else {
      const metaAmount = paymentTokenData?.meta?.china_payment_amount;
      if (metaAmount != null) {
        const parsed = Number(metaAmount);
        setChinaPaymentAmount(Number.isFinite(parsed) ? parsed.toFixed(2) : null);
      } else if (propertyAmount == null) {
        setChinaPaymentAmount(null);
      } else {
        fetchChinaPaymentAmount(propertyAmount, propertyCurrencyCode).then((converted) => {
          if (cancelled) return;
          setChinaPaymentAmount(converted != null ? converted.toFixed(2) : null);
        });
      }
    }

    return () => {
      cancelled = true;
    };
  }, [paymentTokenData?.meta?.china_payment_amount, propertyAmount, propertyCurrencyCode]);

  const showCnyConversion =
    config.IS_CHINA_APP && propertyCurrencyCode !== "cny" && chinaPaymentAmount != null;

  const isConversionRequired =
    totalCharges?.original_currency && totalCharges?.original_currency !== totalCharges?.currency;
  const targetCurrency = (
    isConversionRequired ? totalCharges?.original_currency : totalCharges?.currency
  )?.toLowerCase();

  const originalCurrencyObject = targetCurrency ? findCurrency(targetCurrency) : undefined;

  const conversionToolTipText = isConversionRequired
    ? `You are paying in ${capitalizeFirstLetter(originalCurrencyObject?.short_name)} (${
        originalCurrencyObject?.standard_name
      })
and your total is ${getCurrencySymbol(totalCharges?.original_currency)}${
        totalCharges?.amount
      }. The total you pay 
could differ slightly according to your 
bank’s exchange rates.`
    : "";

  return (
    <div
      className={formatClassNames(
        isInSideInventoryCard
          ? styles.paymentPriceContainerForInvCard
          : styles.paymentPriceContainer,
        !isDesktop && styles.mobile,
        containerClassName,
      )}
    >
      {(isDesktop || customTitle) && (
        <div
          data-testid="payment-page-property-price-summary"
          className={formatClassNames(styles.Heading, customTitle ? styles.withCustomTitle : "")}
        >
          {customTitle || priceSummaryText}
        </div>
      )}
      <div
        className={formatClassNames(
          styles.innerContainer,
          customTitle ? styles.withCustomTitle : "",
        )}
      >
        {paymentInfoData.length > 0 &&
          paymentInfoData.map((payment: any) => (
            <div className={styles.ApartGroup}>
              <div className={formatClassNames(styles.textContainer, styles[userLanguage])}>
                <span className={styles.text}>{t(payment?.label)}</span>
                {payment?.tooltipMsg && (
                  <Tooltip
                    content={t(payment?.tooltipMsg)}
                    direction="bottom"
                    classNameWrapper={formatClassNames(config.IS_CHINA_APP && styles.china)}
                    delay={0}
                  >
                    <div className={styles.tooltipInnerClass}>
                      <InfoIcon height="12px" width="12px" />
                    </div>
                  </Tooltip>
                )}
              </div>
              <div
                data-testid="payment-page-property-deposite-value"
                className={styles.currencyContainer}
              >
                {getCurrencySymbol(payment?.currency)}
                {payment?.amount}
              </div>
            </div>
          ))}
      </div>
      <div className={formatClassNames(styles.divider, footerNote ? styles.withFooterNote : "")} />

      <div
        className={formatClassNames(styles.totalPayable, customTitle ? styles.withCustomTitle : "")}
      >
        <div className={styles.text}>
          {/* // keeping this on FE as there's different text for desktop and confirmed payments */}
          <span>{totalPayableText || t(`${getTotalText({ isConfirmation })}`)}</span>
        </div>
        <div
          data-testid="payment-page-property-total-payable-amount"
          className={formatClassNames(styles.text, styles.totalAmount)}
        >
          {getCurrencySymbol(totalCharges?.currency)}
          {totalCharges?.amount_converted || totalCharges?.amount}
        </div>
      </div>
      {showCnyConversion && (
        <div className={formatClassNames(styles.innerContainer, styles.noPaddingBottom)}>
          <div className={styles.ApartGroup}>
            <div className={formatClassNames(styles.textContainer, styles[userLanguage])}>
              <span className={styles.text}>
                {t("formlayout.inventorypaymentsidecard.convertedAmount")}
              </span>
              <Tooltip
                content={`${t("formlayout.inventorypaymentsidecard.cnyConversionTooltipPart1")} ${(
                  propertyCurrency?.standard_name || ""
                ).toUpperCase()} ${t(
                  "formlayout.inventorypaymentsidecard.cnyConversionTooltipPart2",
                )}`}
                direction="bottom"
                classNameWrapper={formatClassNames(config.IS_CHINA_APP && styles.china)}
                delay={0}
              >
                <div className={styles.tooltipInnerClass}>
                  <InfoIcon height="12px" width="12px" />
                </div>
              </Tooltip>
            </div>
            <div className={styles.currencyContainer}>{`¥${chinaPaymentAmount}`}</div>
          </div>
        </div>
      )}
      {!showCnyConversion && !hideConversionInfo && conversionToolTipText && (
        <div className={styles.conversionTextContainer}>
          <span className={styles.text}>{`You will pay ${getCurrencySymbol(
            inventoryData?.pricing?.original_currency || totalCharges?.original_currency,
          )}${totalCharges?.amount}`}</span>
          <Tooltip
            content={conversionToolTipText}
            direction="bottom"
            classNameWrapper={formatClassNames(
              styles.conversionTooltip,
              config.IS_CHINA_APP && styles.china,
            )}
            delay={0}
          >
            <InfoIcon height="16px" />
          </Tooltip>
        </div>
      )}
      {footerNote && (
        <div className={footerNote.className || styles.footerNote}>{footerNote.text}</div>
      )}
    </div>
  );
};

export default wrapperHOC(InventoryPaymentSideCard, {
  componentName: "InventoryPaymentSideCard-FormLayout-Global",
  showForChina: true,
});
